<!--
  SSE generated images get triggered via an interrupt instead of an imageStyleChoices data message from ably. Using an interrupt here lets us use the same interrupt + resume pattern to pick up user feedback.

  TODO: allow editing the image gen params again.
-->
<template>
  <div class="image-gen-params">
    <div class="image-gen-params__section">
      <div class="image-gen-params__label">{{ t('conversation.imageParams.whatAspectRatio') }}</div>
      <div class="image-gen-params__ratios">
        <BbBtn
          v-for="(ratio, index) in aspectRatios"
          :key="ratio.value"
          :class="['aspect-ratio-button', ratio.class, selectedAspectRatioIndex === index ? 'selected' : '']"
          size="small"
          type="icon"
          no-background
          @click="selectAspectRatio(index, ratio.value)"
        >
          <div class="aspect-ratio-form" :class="ratio.iconClass" />
        </BbBtn>
      </div>
    </div>

    <div class="image-gen-params__section">
      <div class="image-gen-params__label">{{ t('conversation.imageParams.numberOfImages') }}</div>
      <div class="image-gen-params__count">
        <BbBtn
          v-for="n in [1, 2, 3, 4]"
          :key="n"
          size="small"
          :type="numberOfImages === n ? 'primary' : 'secondary'"
          @click="numberOfImages = n"
        >
          {{ n }}
        </BbBtn>
      </div>
    </div>

    <BbSelectList
      v-model="selectedStyle"
      :placeholder="t('conversation.imageParams.wouldYouLikeToUsePredefinedStyle')"
      width="100%"
    >
      <BbSelectListItem v-for="style in brandStylesWithNone" :key="style.id" :label="style.name" :value="style.id">
        <BaseImage v-if="style.url" class="style-preview" :src="style.url" alt="style preview" />
        <div v-else class="style-preview style-preview-placeholder" />
        <div class="style-details">
          <div class="style-name">{{ style.name }}</div>
          <div class="style-description">{{ style.description }}</div>
        </div>
      </BbSelectListItem>
    </BbSelectList>

    <div class="image-gen-params__actions">
      <BbBtn type="primary" size="small" @click="handleSubmit">
        {{ t('common.create') }}
      </BbBtn>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { IMAGE_ASPECT_RATIOS } from '~/constants/conversationModels';
import useImageGeneration from '~/composables/useImageGeneration';
import BaseImage from '~/components/common/BaseImage.vue';

export default {
  name: 'ImageGenParamsSSE',
  components: { BaseImage },
  emits: ['submit'],
  setup(_, { emit }) {
    const { t } = useI18n();
    const { brandStyles } = useImageGeneration();

    const brandStylesWithNone = computed(() => [
      {
        id: 'none',
        value: null,
        name: t('conversation.imageParams.dontUsePredefinedStyle'),
        description: t('conversation.imageParams.promptControlsLookAndFeel'),
        url: null,
      },
      ...(brandStyles.value || []),
    ]);

    const aspectRatios = IMAGE_ASPECT_RATIOS;
    const selectedAspectRatioIndex = ref(0);
    const selectedAspectRatio = ref(IMAGE_ASPECT_RATIOS[0].value);
    const selectedStyle = ref(null);
    const numberOfImages = ref(4);

    const selectAspectRatio = (index, value) => {
      selectedAspectRatioIndex.value = index;
      selectedAspectRatio.value = value;
    };

    /**
     * Emits 'submit' with an {@link import('~/services/api/conversations').ImageGenerationParams}
     * ready to be wrapped in an imageStyleChoicesResponse resume payload.
     */
    const handleSubmit = () => {
      const [width, height] = selectedAspectRatio.value.split(':').map(Number);
      emit('submit', {
        width,
        height,
        brandStyleId: selectedStyle.value === 'none' ? null : selectedStyle.value,
        numberOfImages: numberOfImages.value,
        remixImageId: null,
      });
    };

    return {
      t,
      aspectRatios,
      selectedAspectRatioIndex,
      selectedAspectRatio,
      selectedStyle,
      numberOfImages,
      brandStylesWithNone,
      selectAspectRatio,
      handleSubmit,
    };
  },
};
</script>

<style scoped lang="scss">
.image-gen-params {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-4;

  &__section {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;
  }

  &__label {
    font-size: 13px;
    font-weight: 500;
    color: $bb-neutral-2;
  }

  &__ratios {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-3;
    align-items: center;
  }

  &__count {
    display: flex;
    gap: $bb-spacing-2;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}

.aspect-ratio-button {
  background: transparent !important;
  border: 1px solid $bb-neutral-4 !important;
  border-radius: 0 !important;
  min-width: auto !important;
  width: 48px !important;
  height: 48px !important;

  &.selected {
    border-color: $bb-primary-3 !important;
    border-width: 2px !important;
  }
}

.aspect-ratio-form {
  background: $bb-neutral-4;

  &.vertical-wide { width: 32px; height: 44px; }
  &.vertical      { width: 20px; height: 46px; }
  &.horizontal    { width: 46px; height: 16px; }
  &.horizontal-wide { width: 44px; height: 32px; }
  &.square        { width: 36px; height: 36px; }
}

.bb-select-list {
  :deep(.el-select-dropdown__item) {
    min-width: 0;
    width: 100%;
    height: auto !important;
    min-height: 56px;
    overflow: visible;
    white-space: normal;
    align-items: flex-start !important;
    padding-top: $bb-spacing-2 !important;
    padding-bottom: $bb-spacing-2 !important;
    background-color: $bb-neutral-0;
    border: none !important;
    &.is-selected {
      background-color: transparent !important;
      color: $bb-primary-3 !important;
    }
  }
  :deep(.el-select-dropdown) { min-width: 100%; }

  .bb-select-list-item {
    display: flex;
    flex-direction: row;
    gap: $bb-spacing-2;
    min-width: 0;
    width: 100%;
    padding: $bb-spacing-2 !important;

    .style-preview {
      width: 56px !important;
      height: 40px !important;
      border-radius: $bb-spacing-1;
      flex-shrink: 0;
    }
    .style-preview-placeholder { background-color: $bb-neutral-4; }
    .style-details {
      display: flex;
      flex-direction: column;
      min-width: 0;
      flex: 1;
      overflow: hidden;
    }
    .style-name {
      @include bb-medium-text-styles('md');
      font-weight: 700;
    }
    .style-description {
      @include bb-medium-text-styles('sm');
      word-break: break-word;
      white-space: normal;
    }
  }
}
</style>
